import type { ShippingQuota } from '../api';

/**
 * Player-facing wording for the per-player fulfilment-queue cap.
 *
 * The cap is an operational limit on OUR side, not a punishment, and the
 * player's cards are perfectly safe while it holds — so the copy explains what
 * is happening, gives the real numbers, and says when it clears. Kept in one
 * place so the notice, the picker hint and the submit error never drift apart.
 */

const cards = (n: number) => `${n} ${n === 1 ? 'card' : 'cards'}`;

export const QUOTA_FULL_TITLE = 'Your delivery queue is full';

/** Shown instead of the delivery form when nothing else fits right now. */
export function quotaFullMessage(quota: ShippingQuota): string {
  return (
    `We're already preparing ${cards(quota.inProcessing)} for you, which is the most ` +
    `we handle at once (${quota.limit}). As soon as those are on their way, you'll be able ` +
    'to request another delivery. Your cards stay safe in your collection until then.'
  );
}

/** Shown above the card picker while there is still some room left. */
export function quotaRemainingHint(quota: ShippingQuota): string {
  return (
    `You can add up to ${cards(quota.remaining)} to this delivery — ` +
    `${cards(quota.inProcessing)} of yours ${quota.inProcessing === 1 ? 'is' : 'are'} already being prepared.`
  );
}

/** Shown when the player's basket is larger than the room left. The "already
 *  being prepared" half is dropped when nothing is in the queue — the basket
 *  simply exceeds the per-delivery ceiling, and naming "0 cards" would read as
 *  a glitch. */
export function quotaOverflowMessage(quota: ShippingQuota, selected: number): string {
  const head = `This delivery has ${cards(selected)}, but only ${cards(quota.remaining)} can be added right now`;
  const because =
    quota.inProcessing > 0
      ? ` — ${cards(quota.inProcessing)} of yours ${quota.inProcessing === 1 ? 'is' : 'are'} still being prepared`
      : '';
  return `${head}${because}. Please remove a few and submit the rest later.`;
}

/** Fallback for a backend refusal that arrives without a message of its own
 *  (e.g. a second tab filled the queue between the check and the submit). */
export const QUOTA_SUBMIT_REJECTED_MESSAGE =
  "Your delivery queue is full — we're already preparing the maximum number of cards we handle " +
  'at once. Please try again once those are on their way.';
